import * as amplitude from "@amplitude/analytics-react-native"
import FilledButtonM from "@common/components/button/FilledButtonM"
import IconButtonM from "@common/components/button/IconButtonM"
import TextM from "@common/components/text/TextM"
import { theme } from "@common/theme/theme"
import { IS_WEB, isWebMobile } from "@common/utils/mobileUtils"
import {
    detectStore,
    openStoreUrl,
    resolveStoreUrl,
    trackStoreClick,
} from "@common/utils/storeLinks"
import {
    setLiveWebMobileBannerCollapsed,
    setLiveWebMobileBannerShown,
} from "@data/redux/actions/liveMode"
import { hasRole } from "@data/utility/ability"
import React, { useEffect } from "react"
import { TouchableOpacity, View } from "react-native"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import { connect, useSelector } from "react-redux"

// Hauteurs exposées aux écrans qui calculent leur place restante à la main
const LIVE_WEB_MOBILE_BANNER_HEIGHT = 56
const LIVE_WEB_MOBILE_BANNER_COLLAPSED_HEIGHT = 20

// Le voile du mode focus (GrayOutComponent, zIndex 8) est un frère du bandeau et
// absorbe le premier tap : on passe au-dessus pour que la porte de sortie
// reste accessible du premier coup
const BANNER_Z_INDEX = 9

// Cible tactile minimale recommandée sur mobile
const CLOSE_TOUCH_SIZE = 44

// Le bandeau n'a de sens que dans un navigateur de téléphone / tablette :
// c'est là que le micro, la prise de photo et le retour d'arrière-plan lâchent.
// L'user agent ne bougeant pas en cours de session, la question se tranche une
// fois à l'import plutôt qu'à chaque rendu.
const SHOW_BANNER = IS_WEB && isWebMobile()

export const liveWebMobileBannerHeight = (collapsed) =>
    !SHOW_BANNER
        ? 0
        : collapsed
        ? LIVE_WEB_MOBILE_BANNER_COLLAPSED_HEIGHT
        : LIVE_WEB_MOBILE_BANNER_HEIGHT

// L'élève voit le bandeau à deux endroits selon que la vidéo occupe l'écran
// entier ou qu'elle est réduite, et le tuteur en déduit la place de sa grille :
// l'état "réduit" est donc partagé, et vit dans liveMode comme les autres états
// d'interface de la séance. RESET_LIVE_MODE, dispatché à la sortie du live, le
// remet à zéro — le rappel repart déployé au live suivant sans code dédié.
export const useLiveWebMobileBannerCollapsed = () =>
    useSelector((state) => state.liveMode?.liveWebMobileBannerCollapsed ?? false)

const LiveWebMobileBanner = (props) => {
    // Un admin qui rejoint un live pour superviser passe par l'écran tuteur :
    // sans ce cas, il gonflerait la mesure côté élève. Tant que l'utilisateur
    // n'est pas connu, on n'attribue rien plutôt que de le compter admin.
    const trackedRole = !props.user
        ? null
        : hasRole(props.user, "student")
        ? "student"
        : hasRole(props.user, "tutor")
        ? "tutor"
        : "admin"

    // Les taps partent forcément après hydratation, mais on ne laisse pas la
    // valeur nulle atteindre la mesure
    const tapRole = trackedRole ?? "unknown"

    // L'affichage n'est mesuré qu'une fois par live, quel que soit le nombre de
    // remontages du bandeau quand l'élève bascule plein écran / vidéo réduite.
    // Le drapeau vit dans liveMode : il repart donc à zéro au live suivant, y
    // compris quand on rejoint la même séance après l'avoir quittée.
    useEffect(() => {
        if (!SHOW_BANNER || !trackedRole || props.bannerAlreadyShown) return
        props.setLiveWebMobileBannerShown()
        amplitude.track("live_web_mobile_banner.shown", { role: trackedRole })
    }, [trackedRole, props.bannerAlreadyShown])

    if (!SHOW_BANNER) return null

    const openStore = () => {
        const store = detectStore()
        trackStoreClick(store, "live_web_mobile_banner")
        openStoreUrl(
            resolveStoreUrl(store, {
                iosAppUrl: props.iosAppUrl,
                androidAppUrl: props.androidAppUrl,
            })
        )
    }

    // Pastille : reste visible tout le live, réouvrable au tap
    if (props.collapsed)
        return (
            // Réduit, le bandeau garde sa couleur et sa pleine largeur : il devient
            // une fine ligne orange, sans bande neutre à accorder avec l'écran
            // derrière (il diffère selon l'emplacement) et sans rien recouvrir.
            <TouchableOpacity
                onPress={() => {
                    amplitude.track("live_web_mobile_banner.expanded", {
                        role: tapRole,
                    })
                    props.setLiveWebMobileBannerCollapsed(false)
                }}
                style={{
                    width: "100%",
                    height: LIVE_WEB_MOBILE_BANNER_COLLAPSED_HEIGHT,
                    backgroundColor: theme.colors.warning,
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: BANNER_Z_INDEX,
                }}
            >
                <FontAwesome5
                    name={"mobile-alt"}
                    size={10}
                    color={theme.colors.white}
                    solid
                />
                <TextM
                    color={theme.colors.white}
                    fontWeight={"SemiBold"}
                    fontSize={9}
                    style={{ marginLeft: 6 }}
                >
                    L'APPLI MASTEUR
                </TextM>
            </TouchableOpacity>
        )

    return (
        // Hauteur fixe : le tuteur soustrait cette constante de la place laissée
        // à sa grille, le bandeau ne doit donc jamais grandir avec son texte
        <View
            style={{
                width: "100%",
                height: LIVE_WEB_MOBILE_BANNER_HEIGHT,
                overflow: "hidden",
                zIndex: BANNER_Z_INDEX,
                backgroundColor: theme.colors.warning,
                flexDirection: "row",
                alignItems: "center",
                paddingLeft: 10,
                // laisse la place à la zone tactile de la croix, sinon elle
                // mordrait sur le bouton « Installer »
                paddingRight: CLOSE_TOUCH_SIZE,
            }}
        >
            <FontAwesome5
                name={"mobile-alt"}
                size={14}
                color={theme.colors.white}
                style={{ marginRight: 8 }}
                solid
            />
            <TextM
                wrap
                color={theme.colors.white}
                fontWeight={"SemiBold"}
                fontSize={11}
                style={{ flex: 1, marginRight: 8 }}
            >
                Avec l'appli Masteur, ton live est bien plus stable.
            </TextM>

            {/* Bandeau identique pour l'élève et le tuteur : même message, même CTA */}
            <FilledButtonM
                label="Installer"
                color={theme.colors.white}
                textColor={theme.colors.warning}
                onPress={openStore}
                style={{
                    width: null,
                    marginBottom: 0,
                    borderRadius: 8,
                    alignSelf: "center",
                }}
                contentStyle={{ height: 30 }}
                labelStyle={{ fontSize: 10, marginHorizontal: 8 }}
            />

            <IconButtonM
                icon={"times"}
                iconColor={theme.colors.white}
                size={16}
                // Le glyphe reste petit, mais la zone tactile monte à 44 px :
                // hitSlop n'existe pas sur le web, on dimensionne le conteneur.
                style={{
                    position: "absolute",
                    right: 0,
                    width: CLOSE_TOUCH_SIZE,
                    height: CLOSE_TOUCH_SIZE,
                    margin: 0,
                }}
                onPress={() => {
                    amplitude.track("live_web_mobile_banner.collapsed", {
                        role: tapRole,
                    })
                    props.setLiveWebMobileBannerCollapsed(true)
                }}
            />
        </View>
    )
}

const mapStateToProps = (state) => {
    return {
        user: state.data.currentUser?.object,
        collapsed: state.liveMode?.liveWebMobileBannerCollapsed ?? false,
        bannerAlreadyShown: state.liveMode?.liveWebMobileBannerShown ?? false,
        androidAppUrl: state.data.staticData?.android_app_url,
        iosAppUrl: state.data.staticData?.ios_app_url,
    }
}

const mapDispatchToProps = {
    setLiveWebMobileBannerCollapsed,
    setLiveWebMobileBannerShown,
}

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(LiveWebMobileBanner)
